import { Injectable, inject } from '@angular/core';
import { ListingPhoto } from '../models/listing-photo.model';
import { UploadService } from './upload.service';

@Injectable({
  providedIn: 'root',
})
export class PhotoCropService {
  private readonly uploadService = inject(UploadService);

  async cropCenteredSquare(photo: ListingPhoto): Promise<ListingPhoto> {
    return this.cropCenteredToAspectRatio(photo, 1);
  }

  /**
   * Crops the photo to the given aspect ratio (width/height), centered within the source
   * image. Used for the carousel grid (1:1 via cropCenteredSquare) and the front bumper
   * (a wider rectangle, e.g. 4:3).
   */
  async cropCenteredToAspectRatio(photo: ListingPhoto, aspectRatio: number): Promise<ListingPhoto> {
    const existingDimensions = photo.dimensions?.[photo.order];
    const existingCrop = existingDimensions?.cropDimensions;
    const sourceWidth = photo.width || photo.height || 1;
    const sourceHeight = photo.height || photo.width || 1;
    const isExistingCroppedToRatio = existingCrop
      && Math.abs(
        (sourceWidth * existingCrop.widthPercentage) / (sourceHeight * existingCrop.heightPercentage) - aspectRatio,
      ) < 0.0001;
    if (existingDimensions?.s3Url && isExistingCroppedToRatio) return photo;

    const width = Math.max(Math.round(photo.width || photo.height || 1), 1);
    const height = Math.max(Math.round(photo.height || photo.width || 1), 1);
    const sourceRatio = width / height;
    // Fit the largest box at the target aspect ratio that stays within the source image.
    const cropWidth = sourceRatio > aspectRatio ? height * aspectRatio : width;
    const cropHeight = sourceRatio > aspectRatio ? height : width / aspectRatio;
    const cropDimensions = {
      fromTopPercentage: (1 - cropHeight / height) / 2,
      fromLeftPercentage: (1 - cropWidth / width) / 2,
      widthPercentage: cropWidth / width,
      heightPercentage: cropHeight / height,
    };
    const canvasWidth = 400;
    const canvasHeight = (canvasWidth * height) / width;
    const cropBoxWidth = canvasWidth * (cropWidth / width);
    const cropBoxHeight = canvasHeight * (cropHeight / height);
    const cropData = {
      canvasData: {
        aspectRatio: width / height,
        naturalWidth: width,
        naturalHeight: height,
        width: canvasWidth,
        height: canvasHeight,
        minWidth: canvasWidth,
        minHeight: canvasHeight,
        maxWidth: null,
        maxHeight: null,
        minLeft: 0,
        minTop: 0,
        maxLeft: 0,
        maxTop: 0,
        left: 0,
        top: 0,
        oldLeft: 0,
        oldTop: 0,
      },
      cropBoxData: {
        width: cropBoxWidth,
        height: cropBoxHeight,
        minWidth: 0,
        minHeight: 0,
        maxWidth: canvasWidth,
        maxHeight: canvasHeight,
        minLeft: 0,
        minTop: 0,
        maxLeft: canvasWidth - cropBoxWidth,
        maxTop: canvasHeight - cropBoxHeight,
        left: (canvasWidth - cropBoxWidth) / 2,
        top: (canvasHeight - cropBoxHeight) / 2,
        oldLeft: (canvasWidth - cropBoxWidth) / 2,
        oldTop: (canvasHeight - cropBoxHeight) / 2,
      },
      zoomRatio: canvasWidth / width,
    };
    const response = await this.uploadService.uploadCropData({
      originalFileURL: photo.uri,
      croppedData: {
        aspectRatio,
        dimensions: { cropDimensions, cropData },
        metadata: { aspectRatio },
      },
    }).toPromise();
    const croppedUrl = response?.croppedImageURl;
    if (!croppedUrl) return photo;

    // Keep photo.uri pointing at the original image; the cropped result is only used for rendering
    photo.dimensions = photo.dimensions || {};
    photo.dimensions[photo.order] = { ...existingDimensions, cropDimensions, cropData, s3Url: croppedUrl };
    return photo;
  }
}
